import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { LngLatBoundsLike } from 'mapbox-gl';
import type { RootState } from '../../app/store';
import { ScanData } from '../scanDataApi/scanDataDefinitions';

// Map zoom constants (0 is zoomed all the way out, 23 is all the way in)
export const MAX_ZOOM = 24;
export const BLOCK_LEVEL_ZOOM = 16;

export enum LayerEnum {
  blockNames = 'Block Names',
  blockOutlines = 'Block Outlines',
  heatmap = 'Heatmap',
  markers = 'Markers',
  heatmapCurrentPoint = 'Heatmap Current Point',
  heatmapFadeOutHighlight = 'Heatmap Fade Out Highlight',
}

export type LayerVisibilityValues = {
  [key in LayerEnum]: boolean;
};

export interface MapMarker {
  id: string;
  lat: number;
  lng: number;
}

export enum MapZoomModeEnum {
  blockLevel = 'Zoom to block boundaries',
  pointLevel = 'Zoom to points',
  manual = 'Manual',
}

interface MapState {
  blockButtonWasClicked: boolean;
  layerVisibility: LayerVisibilityValues;
  pointsHeatmapLoading: boolean;
  useCustomHSVSaturation: boolean;
  useCustomHSVValue: boolean;
  HSVSaturation: number;
  HSVValue: number;
  useCustomColor: boolean;
  // specifies range of current bucket
  currMapColorBucket: [number, number] | null;
  hueRotationOffset: number;
  isOpenMeasurementPopup: boolean;
  mapMeasurePoint1: ScanData | null;
  mapMeasurePoint2: ScanData | null;
  mapMarkers: MapMarker[];
  currMapMarkerPopup: MapMarker | null;
  zoomMode: MapZoomModeEnum;
  viewBounds: {
    [MapZoomModeEnum.blockLevel]: LngLatBoundsLike | null;
    [MapZoomModeEnum.pointLevel]: LngLatBoundsLike | null;
    [MapZoomModeEnum.manual]: null;
  };
}

const defaultSaturation = 75;
const defaultValue = 100;

const initialState: MapState = {
  blockButtonWasClicked: false,
  layerVisibility: {
    'Block Names': true,
    'Block Outlines': true,
    Heatmap: true,
    'Heatmap Current Point': true,
    'Heatmap Fade Out Highlight': true,
    Markers: true,
  },
  pointsHeatmapLoading: false,
  useCustomHSVSaturation: true,
  useCustomHSVValue: false,
  HSVSaturation: defaultSaturation,
  HSVValue: defaultValue,
  useCustomColor: true, // this is the basic customer control which just turns on the more advanced qa mode saturation value
  currMapColorBucket: null,
  hueRotationOffset: 30,
  isOpenMeasurementPopup: false,
  mapMeasurePoint1: null,
  mapMeasurePoint2: null,
  mapMarkers: [],
  currMapMarkerPopup: null,
  zoomMode: MapZoomModeEnum.blockLevel,
  viewBounds: {
    [MapZoomModeEnum.blockLevel]: null,
    [MapZoomModeEnum.pointLevel]: null,
    [MapZoomModeEnum.manual]: null,
  },
};

export const mapState = createSlice({
  name: 'mapState',
  initialState,
  reducers: {
    initMapState: () => initialState,
    toggleBlockButtonWasClicked: (state) => {
      // Detect if a block button was pressed.  Useful when users zoom into a block on the map.  If they want to zoom back out to the block boundary by clicking on the block button again, the block id doesn't change so this is used as a backup to trigger a useEffect for this situation.  The actual value isn't important, just used to detect that a button click occurred.
      state.blockButtonWasClicked = !state.blockButtonWasClicked;
    },
    setLayerVisibility: (
      state,
      action: PayloadAction<{ layer: LayerEnum; newValue: boolean }>
    ) => {
      state.layerVisibility[action.payload.layer] = action.payload.newValue;
    },
    setPointsHeatmapLoading: (state, action: PayloadAction<boolean>) => {
      state.pointsHeatmapLoading = action.payload;
    },
    setUseCustomHSVSaturation: (state, action: PayloadAction<boolean>) => {
      state.useCustomHSVSaturation = action.payload;
    },
    setUseCustomHSVValue: (state, action: PayloadAction<boolean>) => {
      state.useCustomHSVValue = action.payload;
    },
    setHSVSaturation: (state, action: PayloadAction<number>) => {
      state.HSVSaturation = action.payload;
    },
    setHSVValue: (state, action: PayloadAction<number>) => {
      state.HSVValue = action.payload;
    },
    setUseCustomColor: (state, action: PayloadAction<boolean>) => {
      state.useCustomColor = action.payload;
      // if turned on, turn on saturation and set to default.  If turned off reset the saturation and value to default values
      if (action.payload) {
        state.HSVSaturation = defaultSaturation;
        state.useCustomHSVSaturation = true;
      } else {
        state.HSVSaturation = defaultSaturation;
        state.HSVValue = defaultValue;
        state.useCustomHSVSaturation = false;
        state.useCustomHSVValue = false;
      }
    },
    setHueRotationOffset: (state, action: PayloadAction<number>) => {
      state.hueRotationOffset = action.payload;
    },
    setCurrMapColorBucket: (
      state,
      action: PayloadAction<[number, number] | null>
    ) => {
      state.currMapColorBucket = action.payload;
    },
    setIsOpenMeasurementPopup: (state, action: PayloadAction<boolean>) => {
      // reset the points if the popup is being closed
      if (action.payload === false) {
        state.mapMeasurePoint1 = null;
        state.mapMeasurePoint2 = null;
      }
      state.isOpenMeasurementPopup = action.payload;
    },
    setMapMeasurePoint1: (state, action: PayloadAction<ScanData | null>) => {
      state.mapMeasurePoint1 = action.payload;
    },
    setMapMeasurePoint2: (state, action: PayloadAction<ScanData | null>) => {
      state.mapMeasurePoint2 = action.payload;
    },
    addMapMarker: (state, action: PayloadAction<MapMarker>) => {
      state.mapMarkers.push(action.payload);
    },
    deleteMapMarker: (state, action: PayloadAction<string>) => {
      state.mapMarkers = state.mapMarkers.filter(
        (marker) => marker.id !== action.payload
      );
    },
    setCurrMapMarkerPopup: (state, action: PayloadAction<MapMarker | null>) => {
      state.currMapMarkerPopup = action.payload;
    },
    setZoomMode: (state, action: PayloadAction<MapZoomModeEnum>) => {
      state.zoomMode = action.payload;
    },
    setBlockViewBounds: (
      state,
      action: PayloadAction<LngLatBoundsLike | null>
    ) => {
      state.viewBounds[MapZoomModeEnum.blockLevel] = action.payload;
    },
    setPointsViewBounds: (
      state,
      action: PayloadAction<LngLatBoundsLike | null>
    ) => {
      state.viewBounds[MapZoomModeEnum.pointLevel] = action.payload;
    },
  },
});

export const {
  initMapState,
  toggleBlockButtonWasClicked,
  setLayerVisibility,
  setPointsHeatmapLoading,
  setUseCustomHSVSaturation,
  setUseCustomHSVValue,
  setHSVSaturation,
  setHSVValue,
  setUseCustomColor,
  setCurrMapColorBucket,
  setHueRotationOffset,
  setIsOpenMeasurementPopup,
  setMapMeasurePoint1,
  setMapMeasurePoint2,
  addMapMarker,
  deleteMapMarker,
  setCurrMapMarkerPopup,
  setZoomMode,
  setBlockViewBounds,
  setPointsViewBounds,
} = mapState.actions;

export const selectBlockButtonWasClicked = (state: RootState) =>
  state.map.blockButtonWasClicked;
export const selectLayerVisibility = (state: RootState) =>
  state.map.layerVisibility;
export const selectPointsHeatmapLoading = (state: RootState) =>
  state.map.pointsHeatmapLoading;
export const selectUseCustomHSVSaturation = (state: RootState) =>
  state.map.useCustomHSVSaturation;
export const selectUseCustomHSVValue = (state: RootState) =>
  state.map.useCustomHSVValue;
export const selectHSVSaturation = (state: RootState) =>
  state.map.HSVSaturation;
export const selectHSVValue = (state: RootState) => state.map.HSVValue;
export const selectUseCustomColor = (state: RootState) =>
  state.map.useCustomColor;
export const selectCurrMapColorBucket = (state: RootState) =>
  state.map.currMapColorBucket;
export const selectHueRotationOffset = (state: RootState) =>
  state.map.hueRotationOffset;
export const selectIsOpenMeasurementPopup = (state: RootState) =>
  state.map.isOpenMeasurementPopup;
export const selectMapMeasurePoint1 = (state: RootState) =>
  state.map.mapMeasurePoint1;
export const selectMapMeasurePoint2 = (state: RootState) =>
  state.map.mapMeasurePoint2;
export const selectMapMarkers = (state: RootState) => state.map.mapMarkers;
export const selectCurrMapMarkerPopup = (state: RootState) =>
  state.map.currMapMarkerPopup;
export const selectZoomMode = (state: RootState) => state.map.zoomMode;
export const selectViewBounds = (state: RootState) => state.map.viewBounds;
export const selectAllMapState = (state: RootState) => state.map;

export default mapState.reducer;
